// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { transformRoute } from "helpers";
import {
  getCronJobExecutionAPI,
  searchCronJobExecutionLogsAPI,
} from "services/api/cronjobs/api";
import {
  CronJobExecution,
  CronJobExecutionLogRow,
  CronJobExecutionLogSearchFormValues,
} from "services/api/cronjobs/types";
import {
  initialCronJobExecutionLogPagination,
  initialCronJobExecutionLogSearchForm,
} from "services/api/cronjobs/consts";
import { DataWithPagination } from "services/api";
import { Pagination } from "services/search";

// Tipovi i konstante
import { CronJobExecutionPageCmp, CronJobExecutionPageProps } from "./types";
import { CRON_JOB_EXECUTION_LOG_COLUMNS } from "./consts";
import {
  CRON_JOB_DETAIL_PAGE_TITLE,
  CRON_JOB_EXECUTION_PAGE_TITLE,
  CRON_JOB_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Loader } from "ui/components/Loader";
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import CronJobExecutionInfoCard from "./components/CronJobExecutionInfoCard/CronJobExecutionInfoCard";
import CronJobExecutionLogsFilters from "./components/CronJobExecutionLogsFilters/CronJobExecutionLogsFilters";

const emptyLogs: DataWithPagination<CronJobExecutionLogRow> = {
  data: [],
  pagination: initialCronJobExecutionLogPagination,
};

/**
 * Log jedne obrade, kronološki. „Osvježi" postoji jer obrada u tijeku piše dok ekran stoji.
 */
const CronJobExecutionPage: CronJobExecutionPageCmp = (_props: CronJobExecutionPageProps) => {
  const { cronJobId, executionId } = useParams<{ cronJobId: string; executionId: string }>();

  const [execution, setExecution] = useState<CronJobExecution>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<DataWithPagination<CronJobExecutionLogRow>>(emptyLogs);
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useState<CronJobExecutionLogSearchFormValues>(
    initialCronJobExecutionLogSearchForm
  );

  const fetchExecution = useCallback(async () => {
    if (!executionId) return;
    setIsLoading(true);
    try {
      setExecution(await getCronJobExecutionAPI(executionId));
    } catch {
      // greška je prikazana kroz snackbar
    } finally {
      setIsLoading(false);
    }
  }, [executionId]);

  const fetchLogs = useCallback(
    async (pagination: Pagination, filters: CronJobExecutionLogSearchFormValues) => {
      if (!executionId) return;
      setIsLoadingLogs(true);
      try {
        setLogs(await searchCronJobExecutionLogsAPI(executionId, pagination, filters));
      } catch {
        setLogs(emptyLogs);
      } finally {
        setIsLoadingLogs(false);
      }
    },
    [executionId]
  );

  useEffect(() => {
    fetchExecution();
    fetchLogs(initialCronJobExecutionLogPagination, initialCronJobExecutionLogSearchForm);
  }, [fetchExecution, fetchLogs]);

  const handleRefresh = useCallback(() => {
    fetchExecution();
    fetchLogs(logs.pagination, filterValues);
  }, [fetchExecution, fetchLogs, logs.pagination, filterValues]);

  const handleSearchChange = useCallback(
    (values: CronJobExecutionLogSearchFormValues) => {
      setFilterValues(values);
      fetchLogs({ ...logs.pagination, pageNumber: 1 }, values);
    },
    [fetchLogs, logs.pagination]
  );

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchLogs(pagination, filterValues);
    },
    [fetchLogs, filterValues]
  );

  const title = CRON_JOB_EXECUTION_PAGE_TITLE;
  const breadcrumbs: BreadcrumbItem[] = [
    { text: CRON_JOB_SEARCH_PAGE_TITLE.text, route: CRON_JOB_SEARCH_PAGE_TITLE.route },
    ...(cronJobId
      ? [
          {
            text: execution?.cronJobName ?? CRON_JOB_DETAIL_PAGE_TITLE.text,
            route: transformRoute(Routes.CronJobDetailPageRouter, { cronJobId }),
          },
        ]
      : []),
  ];

  if (!cronJobId || !executionId) return <></>;

  return (
    <div className="">
      <div className="bg-white rounded-t-md border-b border-slate-200">
        <PageHeader>
          <div className="flex justify-between">
            <div>
              <PageTitle
                title={
                  execution
                    ? `${title.text} #${execution.id} [${execution.cronJobName ?? execution.cronJobId}]`
                    : `${title.text} #${executionId}`
                }
                icon={title.icon}
                iconColor={title.iconColor}
              />
              <Breadcrumb items={breadcrumbs} />
            </div>
            <div className="flex gap-2 items-center">
              <Button
                variant="outlined"
                startIcon="/media/icons/refresh.svg"
                onClick={handleRefresh}
                disabled={isLoading || isLoadingLogs}
              >
                Osvježi
              </Button>
            </div>
          </div>
        </PageHeader>
      </div>

      <div className="px-4 py-6 flex flex-col gap-6">
        {isLoading || !execution ? (
          <Loader isLoading={true}> </Loader>
        ) : (
          <CronJobExecutionInfoCard execution={execution} />
        )}

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-base font-bold text-slate-600">Log obrade</p>
            <CronJobExecutionLogsFilters
              formValues={filterValues}
              onSearchChange={handleSearchChange}
            />
          </div>
          <div className="bg-white border border-slate-200 rounded-sm">
            {!isLoadingLogs && logs.pagination.totalPages === 0 ? (
              <NoResults
                message={
                  filterValues.search
                    ? "Nijedan zapis ne sadrži traženi tekst"
                    : "Ova obrada nema log zapisa"
                }
              />
            ) : (
              <TableSearch
                columns={CRON_JOB_EXECUTION_LOG_COLUMNS}
                data={logs.data}
                selectableRows={false}
                pagination={logs.pagination}
                onPaginationChange={handlePaginationChange}
                isLoading={isLoadingLogs}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CronJobExecutionPage.route = Routes.CronJobExecutionPageRouter;

export default CronJobExecutionPage;
